import React, {
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
  useState,
} from "react";
import Reveal from "reveal.js";
import parse from "html-react-parser";
import "reveal.js/dist/reveal.css";
import Markdown from "reveal.js/plugin/markdown/markdown.esm";
import Highlight from "reveal.js/plugin/highlight/highlight.esm";
import Maths from "reveal.js/plugin/math/math.esm";
import Notes from "reveal.js/plugin/notes/notes.esm";
import Search from "reveal.js/plugin/search/search.esm";
import Zoom from "reveal.js/plugin/zoom/zoom.esm";
import "reveal.js/plugin/highlight/monokai.css";
import "reveal.js/dist/theme/white.css";
import styles from "./reveal-slide.module.scss";
import { logger } from '../../utils/logger';

function RevealSlide(props: any) {
  const deckDivRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const deckRef = useRef<Reveal.Api | null>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const { fullScreen, style } = props;

  useEffect(() => {
    if (deckRef.current) {
      return;
    }
    let revealOptions: any = getConfigOptions();

    try {
      deckRef.current = new Reveal(deckDivRef.current!, revealOptions);

      deckRef.current.initialize().then(() => {
        const totalSlides = deckRef.current?.getTotalSlides();
        props.setTotalPages && props.setTotalPages(totalSlides);
      });
    } catch (e) {
      logger.warn("Reveal.js init call failed.", e);
    }
    return () => {
      try {
        if (deckRef.current) {
          deckRef.current.destroy();
          deckRef.current = null;
        }
      } catch (e) {
        logger.warn("Reveal.js destroy call failed.", e);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef?.current?.getBoundingClientRect();

      setWidth(width);
      setHeight(height);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("orientationchange", calculateSize);

    return () => {
      window.removeEventListener("orientationchange", calculateSize);
    };
  }, [calculateSize]);

  useLayoutEffect(() => {
    window.addEventListener("resize", calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener("resize", calculateSize);
    };
  }, [calculateSize]);

  useEffect(() => {
    if (width) {
      setTimeout(calculateSize, 50);
    }
  }, [calculateSize, width]);

  useEffect(() => {
    if (props.data && deckRef.current) {
      setScale();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScreen, deckRef.current]);

  useEffect(() => {
    if (props.data && deckRef.current) {
      updateScale();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style, height]);

  const getConfigOptions = () => {
    return {
      backgroundTransition: "slide",
      transition: "slide",
      center: true,
      margin: 0,
      width: 960,
      height: 540,
      embedded: true,
      progress: false,
      controls: !!props.controls,
      minScale: 0.2,
      maxScale: 2.0,
      plugins: [Markdown, Highlight, Maths, Notes, Search, Zoom],
    };
  };

  const setScale = () => {
    if (props.data && deckRef.current) {
      const scale = deckRef?.current?.getScale();
      let revealOptions: any = getConfigOptions();

      if (
        props.fullScreen &&
        scale > 1 &&
        !!height &&
        props.style.height > height
      ) {
        const diff = props.style.height - height;
        const percentage = (diff / height) * 100;
        const minScale = scale * (1 + percentage / 100);
        revealOptions = {
          ...revealOptions,
          minScale: minScale < 2.3 ? minScale : 0.2,
          maxScale: 2.5,
        };
      }
      deckRef?.current?.configure(revealOptions);
      deckRef?.current?.layout();
    }
  };
  const updateScale = () => {
    if (props.data && deckRef.current) {
      const scale = deckRef?.current?.getScale();
      let revealOptions: any = getConfigOptions();

      if (
        props.fullScreen &&
        scale > 1 &&
        !!height &&
        props.style.height > height
      ) {
        const diff = props.style.height - height;
        const percentage = (diff / height) * 100;
        const minScale = scale * (1 + percentage / 100);
        revealOptions = {
          ...revealOptions,
          minScale: minScale < 2.3 ? minScale : 0.2,
          maxScale: 2.5,
        };
        deckRef?.current?.configure(revealOptions);
        deckRef?.current?.layout();
      }
    }
  };

  useEffect(() => {
    if (props.data && deckRef.current && props.pageNumber) {
      if (props.pageNumber > props.totalPages) return;
      const progress = deckRef.current?.getProgress();
      const currentPage = progress * props.totalPages;
      if (currentPage !== props.pageNumber) {
        if (currentPage < props.pageNumber) {
          deckRef?.current?.next();
        } else {
          deckRef?.current?.prev();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.pageNumber]);

  useEffect(() => {
    if (props.data && deckRef.current && props.pageNumber) {
      deckRef?.current?.prev();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.totalPages]);

  if (!props.data) return null;

  return (
    <div
      className={
        (props.fullScreen && !props.controls) ||
        (!props.controls && !props.isPreview)
          ? `reveal`
          : `reveal ${styles.revealViewport}`
      }
      ref={deckDivRef}
    >
      <div className="slides" ref={wrapperRef}>
        {parse(props.data)}
      </div>
    </div>
  );
}

export default RevealSlide;
